/**
 * Enemy - Base enemy entity that marches toward the player
 *
 * Features:
 * - Triangle shape pointing down
 * - Marches straight down at constant rate
 * - Takes damage from bullets, dies at 0 HP
 * - Death animation with particle fragments
 */

import Phaser from 'phaser';
import { COLORS, DURATIONS, GAME_HEIGHT } from '../config';
import { EFFECTS, ENEMIES } from '../utils/constants';
import { audioSystem } from '../systems/AudioSystem';
import { getParticlePool } from '../systems/ParticlePool';

export interface EnemyConfig {
  x: number;
  y: number;
  hp?: number;
  speed?: number;
  isFinale?: boolean;
  /** Pooled enemies return to an inactive state after their death animation. */
  pooled?: boolean;
}

export class Enemy extends Phaser.GameObjects.Container {
  /** Size of the enemy (triangle width/height) */
  static readonly FRAME_SIZE = 48;
  static readonly SCALE = 0.68;
  static readonly SIZE = Enemy.FRAME_SIZE * Enemy.SCALE;

  /** Current HP */
  private hp: number;

  /** Maximum HP (for HP bar display on elites) */
  protected maxHp: number;

  /** Movement speed multiplier */
  private speed: number;

  /** Points awarded when killed */
  protected points: number;

  /** The enemy sprite */
  protected sprite: Phaser.GameObjects.Sprite;

  /** Whether the enemy is alive */
  private isAlive: boolean = true;

  /** Finale enemies are mandatory targets; contact or leakage fails the run. */
  private readonly isFinaleTarget: boolean;
  private readonly isPooled: boolean;

  /** Reference to the scene's scroll speed */
  private scrollSpeed: number = 100;

  /** Active hit tween (to avoid stacking) */
  private hitTween: Phaser.Tweens.Tween | null = null;

  constructor(scene: Phaser.Scene, config: EnemyConfig) {
    super(scene, config.x, config.y);

    this.hp = config.hp ?? ENEMIES.basic.hp;
    this.maxHp = this.hp;
    this.speed = config.speed ?? ENEMIES.basic.speed;
    this.points = ENEMIES.basic.points;
    this.isFinaleTarget = config.isFinale ?? false;
    this.isPooled = config.pooled ?? false;

    // Create the sprite
    this.sprite = scene.add.sprite(0, 0, 'enemy-basic', 0);
    this.sprite.setScale(Enemy.SCALE);
    if (this.sprite.anims) {
      this.sprite.play('lobster_walk');
    }

    // Dark outline glow so the sprite reads clearly at small scale
    if (this.sprite.preFX) {
      this.sprite.preFX.addGlow(0x000000, 1, 0, false, 0.1, 16);
    }

    this.add(this.sprite);

    scene.add.existing(this);
  }

  /**
   * Sets the scroll speed (called by GameScene)
   */
  setScrollSpeed(speed: number): void {
    this.scrollSpeed = speed;
  }

  /**
   * Updates enemy position each frame
   */
  update(_time: number, delta: number): void {
    if (!this.isAlive) return;

    // Move down at scroll speed * enemy speed multiplier
    const movement = (this.scrollSpeed * this.speed * delta) / 1000;
    this.y += movement;

    // Check if off-screen (below game area)
    if (this.y > GAME_HEIGHT + Enemy.SIZE) {
      this.removeFromPlay();
    }
  }

  /**
   * Takes damage from a hit
   * @returns true if the enemy died
   */
  takeDamage(damage: number): boolean {
    if (!this.isAlive) return false;

    this.hp -= damage;

    // Flash white on hit
    this.flashWhite();

    // Small hit pulse for snappy feedback
    if (this.hp > 0) {
      if (this.sprite.anims) {
        this.sprite.play('lobster_hit', true);
        this.sprite.once('animationcomplete', () => {
          if (this.isAlive) {
            this.sprite.play('lobster_walk', true);
          }
        });
      }
      this.playHitPulse();
      this.spawnHitStreak();
    }

    if (this.hp <= 0) {
      this.die();
      return true;
    }

    return false;
  }

  /**
   * Flash white effect on taking damage
   */
  private flashWhite(): void {
    this.sprite.setTintFill(0xFFFFFF);
    this.scene.time.delayedCall(DURATIONS.hitFlash, () => {
      if (this.isAlive) {
        this.sprite.clearTint();
      }
    });
  }

  /**
   * Short trailing streak to give hits more weight
   */
  private spawnHitStreak(): void {
    const streak = this.scene.add.graphics();
    const size = Enemy.SIZE;

    streak.lineStyle(2, COLORS.enemy, 0.7);
    streak.beginPath();
    // Trail behind movement (enemy moves down, so streak above)
    streak.moveTo(0, -size / 2);
    streak.lineTo(0, -size / 2 - 10);
    streak.closePath();
    streak.strokePath();

    streak.setPosition(this.x, this.y);
    streak.setDepth(this.depth - 1);

    this.scene.tweens.add({
      targets: streak,
      y: this.y - 6,
      alpha: 0,
      duration: 120,
      ease: 'Cubic.easeOut',
      onComplete: () => streak.destroy(),
    });
  }

  /**
   * Small scale pulse when hit
   */
  private playHitPulse(): void {
    // Reset any existing tween to keep motion snappy
    if (this.hitTween) {
      this.hitTween.stop();
      this.hitTween = null;
    }

    this.setScale(1);

    const { scale, duration } = EFFECTS.enemyHitPulse;
    this.hitTween = this.scene.tweens.add({
      targets: this,
      scaleX: scale,
      scaleY: scale,
      duration: duration,
      ease: 'Back.easeOut',
      yoyo: true,
      onComplete: () => {
        this.setScale(1);
        this.hitTween = null;
      },
    });
  }

  /**
   * Enemy death sequence
   */
  protected die(): void {
    this.isAlive = false;

    // Emit death event with points
    this.emit('died', this.points);

    // Play enemy death sound
    audioSystem.play('enemy_death');

    if (this.sprite.anims) {
      this.sprite.play('lobster_death');
    }

    // Death animation: scale up, slight rotate, and fade
    const rotation = Phaser.Math.FloatBetween(-EFFECTS.enemyDeathPop.rotationMax, EFFECTS.enemyDeathPop.rotationMax);
    this.scene.tweens.add({
      targets: this,
      scaleX: EFFECTS.enemyDeathPop.scale,
      scaleY: EFFECTS.enemyDeathPop.scale,
      rotation: rotation,
      alpha: 0,
      duration: DURATIONS.enemyDeath,
      ease: 'Power2',
      onComplete: () => {
        this.createDeathParticles();
        this.removeFromPlay();
      },
    });
  }

  /** Restores all mutable combat/visual state before a pooled enemy is reused. */
  resetForReuse(x: number, y: number, hp: number = ENEMIES.basic.hp): void {
    if (this.hitTween) {
      this.hitTween.stop();
      this.hitTween = null;
    }
    this.hp = Math.max(1, Math.round(hp));
    this.maxHp = this.hp;
    this.speed = ENEMIES.basic.speed;
    this.points = ENEMIES.basic.points;
    this.isAlive = true;
    this.setPosition(x, y);
    this.setActive(true);
    this.setVisible(true);
    this.setScale(1);
    this.setAlpha(1);
    this.setRotation(0);
    this.sprite.clearTint();
    if (this.sprite.anims) {
      this.sprite.play('lobster_walk', true);
    }
  }

  /** Removes an enemy safely whether it is pooled or individually allocated. */
  removeFromPlay(): void {
    this.isAlive = false;
    if (this.isPooled) {
      // A leak/collision does not emit `died`, so its one-shot scoring listener
      // would otherwise survive into the next reuse and double-count a later
      // kill. Death paths have already emitted and cleared the same listener.
      this.removeAllListeners('died');
      this.setActive(false);
      this.setVisible(false);
      this.setPosition(-100, -100);
      this.emit('readyForPool');
      return;
    }
    this.destroy();
  }

  /**
   * Creates particle fragments on death
   * Uses particle pool for better performance
   */
  protected createDeathParticles(): void {
    const pool = getParticlePool();

    if (pool) {
      // Use pooled particles (performance optimized)
      pool.spawnBurst(this.x, this.y, 'triangle', COLORS.enemy, Phaser.Math.Between(5, 8), {
        minSize: Enemy.SIZE / 4,
        maxSize: Enemy.SIZE / 2,
        minVelocity: 50,
        maxVelocity: 100,
        minDuration: 250,
        maxDuration: 350,
        alpha: 0.8,
      });
    } else {
      // Fallback: create particles manually (pool not initialized)
      const particleCount = Phaser.Math.Between(5, 8);
      const size = Enemy.SIZE / 3;

      for (let i = 0; i < particleCount; i++) {
        const particle = this.scene.add.graphics();
        particle.fillStyle(COLORS.enemy, 0.8);

        // Small triangle fragment
        particle.beginPath();
        particle.moveTo(0, size / 2);
        particle.lineTo(-size / 2, -size / 2);
        particle.lineTo(size / 2, -size / 2);
        particle.closePath();
        particle.fillPath();

        particle.setPosition(this.x, this.y);

        // Random angle and velocity
        const angle = (i / particleCount) * Math.PI * 2;
        const velocity = Phaser.Math.Between(50, 100);
        const vx = Math.cos(angle) * velocity;
        const vy = Math.sin(angle) * velocity;
        const rotation = Phaser.Math.FloatBetween(-Math.PI, Math.PI);

        // Animate particle outward and fade
        this.scene.tweens.add({
          targets: particle,
          x: particle.x + vx * 0.3,
          y: particle.y + vy * 0.3,
          alpha: 0,
          rotation: rotation,
          duration: 300,
          ease: 'Power2',
          onComplete: () => particle.destroy(),
        });
      }
    }
  }

  /**
   * Gets current HP
   */
  getHp(): number {
    return this.hp;
  }

  /**
   * Gets max HP
   */
  getMaxHp(): number {
    return this.maxHp;
  }

  /**
   * Gets points value
   */
  getPoints(): number {
    return this.points;
  }

  /**
   * Returns whether enemy is alive
   */
  getIsAlive(): boolean {
    return this.isAlive;
  }

  getIsFinale(): boolean {
    return this.isFinaleTarget;
  }

  /**
   * Gets the collision radius for this enemy
   */
  getCollisionRadius(): number {
    return Enemy.SIZE / 2;
  }

  /**
   * Cleanup
   */
  destroy(fromScene?: boolean): void {
    this.isAlive = false;
    if (this.hitTween) {
      this.hitTween.stop();
      this.hitTween = null;
    }
    super.destroy(fromScene);
  }
}
